import React, { useEffect, useRef, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import tinycolor from 'tinycolor2'
import 'canvas-datagrid'
import { useTheme } from '@mui/material'
import validators from '../../../../../../utils/validators'

function hasError(value, row, column) {
  if (
    (typeof value === 'undefined' && column === 0 && row > -1) ||
    (value === '' && column === 0 && row > -1)
  ) {
    return true
  } else if (!validators.isValidNChassis(value) && column === 0 && row > -1) {
    return true
  }
  if (
    (typeof value === 'undefined' && column === 1 && row > -1) ||
    (value === '' && column === 1 && row > -1)
  ) {
    return true
  } else if (!validators.isValidNParc(value) && column === 1 && row > -1) {
    return true
  }
  if (
    (typeof value === 'undefined' && column === 2 && row > -1) ||
    (value === '' && column === 2 && row > -1)
  ) {
    return true
  }
  return false
}

const CanvasDatagrid = forwardRef((props, parentRef) => {
  const gridRef = useRef(null)

  const theme = useTheme()
  const { t } = useTranslation()

  const {
    errors = new Set(),
    errorMessages = {},
    corrections = {},
    onRevertCorrection,
    onManualCorrection,
  } = props

  // Use refs to maintain current values for event handlers
  const errorsRef = useRef(errors)
  const errorMessagesRef = useRef(errorMessages)
  const correctionsRef = useRef(corrections)
  const onRevertCorrectionRef = useRef(onRevertCorrection)
  const onManualCorrectionRef = useRef(onManualCorrection)

  // Update refs when props change
  useEffect(() => {
    errorsRef.current = errors
    errorMessagesRef.current = errorMessages
    correctionsRef.current = corrections
    onRevertCorrectionRef.current = onRevertCorrection
    onManualCorrectionRef.current = onManualCorrection
  }, [
    errors,
    errorMessages,
    corrections,
    onRevertCorrection,
    onManualCorrection,
  ])

  useEffect(() => {
    gridRef.current.data = props.data
  }, [props.data])

  // Force re-render when errors or corrections change
  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.draw()
    }
  }, [errors, errorMessages, corrections])

  useEffect(() => {
    const grid = gridRef.current
    if (!grid) return

    const handleEndEdit = (e) => {
      const newValueHasError = hasError(
        e.value,
        e.cell.rowIndex,
        e.cell.columnIndex
      )
      const oldValueHasError = hasError(
        e.cell.value,
        e.cell.rowIndex,
        e.cell.columnIndex
      )

      if (props.onCellEdited) {
        props.onCellEdited(e.cell.rowIndex, e.cell.columnIndex, e.value)
      }

      // Point 4: if cell had a suggestion correction, record the manual correction
      const cellKey = `${e.cell.rowIndex}-${e.cell.columnIndex}`
      const correction = correctionsRef.current[cellKey]
      if (correction?.type === 'suggestion' && onManualCorrectionRef.current) {
        onManualCorrectionRef.current(
          e.cell.rowIndex,
          e.cell.columnIndex,
          correction.originalValue,
          e.value
        )
      }

      if (newValueHasError && !oldValueHasError) {
        props.onEditFailure(e.cell.rowIndex, e.cell.columnIndex)
      } else if (!newValueHasError && oldValueHasError) {
        props.onEditSuccess(e.cell.rowIndex, e.cell.columnIndex)
      }
    }

    grid.removeEventListener('endedit', handleEndEdit)
    grid.addEventListener('endedit', handleEndEdit)

    gridRef.current.removeEventListener('contextmenu')
    gridRef.current.addEventListener('contextmenu', (e) => {
      if (!e.cell || e.cell.rowIndex === undefined) return
      e.items.splice(0, e.items.length)

      // Point 3: revert auto-correction option
      const cellKey = `${e.cell.rowIndex}-${e.cell.columnIndex}`
      const correction = correctionsRef.current[cellKey]
      if (correction?.type === 'auto') {
        e.items.push({
          title: t(
            'create-chassis-dialog.bulk-upload-wizard.context-menu-revert-correction'
          ),
          click() {
            if (onRevertCorrectionRef.current) {
              onRevertCorrectionRef.current(
                e.cell.rowIndex,
                e.cell.columnIndex,
                correction.originalValue
              )
            }
          },
        })
      }

      const hasAddOption = e.items.some(
        (item) =>
          item.title ===
          t(
            'create-vehicle-type-dialog.bulk-upload-wizard.preview-menu-add-row'
          )
      )
      if (!hasAddOption) {
        e.items.push({
          title: t(
            'create-vehicle-type-dialog.bulk-upload-wizard.preview-menu-add-row'
          ),
          click() {
            gridRef.current.addRow({})
            props.onAddRow(props.data.length - 1)
          },
        })
      }

      const hasDeleteOption = e.items.some(
        (item) =>
          item.title ===
          t(
            'create-vehicle-type-dialog.bulk-upload-wizard.preview-menu-delete-row'
          )
      )
      if (!hasDeleteOption) {
        e.items.push({
          title: t(
            'create-vehicle-type-dialog.bulk-upload-wizard.preview-menu-delete-row'
          ),
          click() {
            gridRef.current.deleteRow(e.cell.rowIndex)
            props.onDeleteRow(e.cell.rowIndex)
          },
        })
      }
    })

    gridRef.current.removeEventListener('rendercell')
    gridRef.current.addEventListener('rendercell', (e) => {
      const cellKey = `${e.cell.rowIndex}-${e.cell.columnIndex}`

      const correction = correctionsRef.current[cellKey]
      const hasValidationError =
        errorsRef.current.has(cellKey) || errorMessagesRef.current[cellKey]
      const cellHasError = hasError(
        e.value,
        e.cell.rowIndex,
        e.cell.columnIndex
      )

      // Errors always take priority — an auto-correction may itself be wrong
      if (cellHasError || hasValidationError) {
        e.ctx.fillStyle = tinycolor(theme.palette.danger.main)
          .lighten(30)
          .toHexString()
      } else if (correction?.type === 'auto') {
        e.ctx.fillStyle = tinycolor(theme.palette.warning.light).toHexString()
      } else if (correction?.type === 'suggestion') {
        e.ctx.fillStyle = '#FFF9C4' // light yellow
      } else if (e.cell.rowIndex !== -1 && e.cell.columnIndex !== -1) {
        e.ctx.fillStyle = theme.palette.secondary.contrastText
      }
    })

    // Mousemove for tooltips
    gridRef.current.removeEventListener('mousemove')
    gridRef.current.addEventListener('mousemove', (e) => {
      const cellKey = `${e.cell.rowIndex}-${e.cell.columnIndex}`
      const errorMessage = errorMessagesRef.current[cellKey]
      const correction = correctionsRef.current[cellKey]

      const parts = []
      if (correction?.type === 'auto') {
        parts.push(
          t(
            'create-chassis-dialog.bulk-upload-wizard.cell-tooltip-auto-corrected',
            {
              original: correction.originalValue,
              corrected: correction.correctedValue,
            }
          )
        )
      } else if (correction?.type === 'suggestion') {
        parts.push(
          t(
            'create-chassis-dialog.bulk-upload-wizard.cell-tooltip-suggestion',
            {
              corrected: correction.correctedValue,
            }
          )
        )
      }
      if (errorMessage && errorMessage.length > 0) {
        parts.push(errorMessage.join('\n'))
      }

      gridRef.current.canvas.title = parts.join('\n') || ''
    })
  }, [
    props.onCellEdited,
    props.onEditSuccess,
    props.onEditFailure,
    props.onAddRow,
    props.onDeleteRow,
    props.data.length,
    t,
    theme.palette,
  ])

  // On unmount
  useEffect(() => {
    const ref = gridRef.current
    return () => {
      if (ref) {
        ref.removeEventListener('endedit')
        ref.removeEventListener('rendercell')
        ref.removeEventListener('contextmenu')
        ref.removeEventListener('mousemove')
        if (ref.input) {
          ref.input.remove()
        }
        ref.dispose()
      }
    }
  }, [])

  return (
    <canvas-datagrid
      style={{
        width: '100%',
        height: '100%',
        editCellBackgroundColor: theme.palette.secondary.contrastText,
        gridBackgroundColor: theme.palette.secondary.contrastText,
        rowHeaderCellBackgroundColor: theme.palette.secondary.contrastText,
        columnHeaderCellBackgroundColor: theme.palette.secondary.contrastText,
        cellBorderColor: theme.palette.captGrey.light,
        columnHeaderCellBorderColor: theme.palette.captGrey.light,
        columnHeaderCellCapBorderColor: theme.palette.captGrey.light,
        columnHeaderCellCapBackgroundColor:
          theme.palette.secondary.contrastText,
        columnHeaderCellHoverBackgroundColor: theme.palette.background.light,
        rowHeaderCellHoverBackgroundColor: theme.palette.background.light,
        rowHeaderCellBorderColor: theme.palette.captGrey.light,
        rowHeaderCellSelectedColor: theme.palette.secondary.main,
        activeRowHeaderCellBackgroundColor: theme.palette.primary.background,
        activeColumnHeaderCellBackgroundColor: theme.palette.primary.background,
        activeHeaderCellBackgroundColor: theme.palette.primary.background,
        columnHeaderOrderByArrowColor: theme.palette.primary.main,
        activeCellOverlayBorderColor: theme.palette.primary.main,
        activeCellBorderColor: theme.palette.primary.main,
        activeCellSelectedBackgroundColor: theme.palette.background.light,
        cornerCellBackgroundColor: theme.palette.background.light,
        contextMenuBackground: theme.palette.background.light,
        contextMenuHoverColor: theme.palette.primary.main,
        contextMenuHoverBackground: theme.palette.primary.background,
        contextMenuArrowColor: theme.palette.primary.main,
        cellWhiteSpace: 'normal',
      }}
      ref={(ref) => {
        gridRef.current = ref
        if (parentRef) {
          parentRef.current = ref
        }
      }}
      editable
      multiLine
      autoResizeRows
      allowRowResizeFromCell
      allowColumnResizeFromCell
    />
  )
})

CanvasDatagrid.displayName = 'CanvasDatagrid'
export default CanvasDatagrid
